import { getInitials } from '../../lib/initials';
import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { getMessages, sendMessage as sendMessageAPI, type Message } from '../../services/api/messages';
import { getUserById } from '../../services/users';
import { MessageBubble } from './MessageBubble';
import { MessageComposer } from './MessageComposer';
import { useAuth } from '../../hooks/useAuth';

export const ConversationView = () => {
  const { userId: otherUserId } = useParams<{ userId: string }>();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Fetch other user's profile
  const { data: otherUser } = useQuery({
    queryKey: ['user', otherUserId],
    queryFn: () => getUserById(otherUserId!),
    enabled: !!otherUserId,
  });

  // Fetch messages
  const { data: messages, isLoading, error } = useQuery({
    queryKey: ['messages', otherUserId],
    queryFn: () => getMessages(otherUserId!),
    enabled: !!otherUserId,
    refetchInterval: 5000, // Poll every 5 seconds for new messages
  });

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: (content: string) =>
      sendMessageAPI({
        receiverId: otherUserId!,
        content,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages', otherUserId] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['messages', 'unread-count'] });
    },
  });

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (!otherUserId) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-gray-500">Ingen samtale valgt</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Kunne ikke indlæse beskeder</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b p-4 bg-white flex items-center gap-3">
        <Link to="/messages" className="text-blue-600 hover:text-blue-800">
          ← Tilbage
        </Link>
        {otherUser && (
          <>
            <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center overflow-hidden">
              {otherUser.profileImageUrl ? (
                <img
                  src={otherUser.profileImageUrl}
                  alt={otherUser.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-lg text-gray-600">
                  {getInitials(otherUser.name)}
                </span>
              )}
            </div>
            <Link
              to={`/profile/${otherUser.id}`}
              className="font-semibold text-gray-900 hover:text-blue-600"
            >
              {otherUser.name}
            </Link>
          </>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
        {messages && messages.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>Start samtalen ved at sende en besked</p>
          </div>
        ) : (
          <>
            {messages?.map((message: Message) => (
              <MessageBubble
                key={message.id}
                message={message}
                isMine={message.senderId === user?.id}
              />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Composer */}
      <MessageComposer
        onSend={(content) => sendMessageMutation.mutate(content)}
        disabled={sendMessageMutation.isPending}
      />
    </div>
  );
};
